/**
 * useEnrollments Hook
 * React Query hook para gestión de enrollments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enrollmentsApi } from '@/lib/api/enrollments';
import { useEnrollmentsStore } from '@/store/enrollmentsStore';

const QUERY_KEYS = {
  enrollments: ['enrollments'] as const,
  rejected: ['enrollments', 'rejected'] as const,
};

/**
 * Hook para cargar enrollments pendientes
 */
export const useEnrollments = () => {
  const { setEnrollments } = useEnrollmentsStore();

  return useQuery({
    queryKey: QUERY_KEYS.enrollments,
    queryFn: async () => {
      const enrollments = await enrollmentsApi.getPendingEnrollments();
      setEnrollments(enrollments);
      return enrollments;
    },
    refetchInterval: 30000, // Refetch every 30s as fallback
    staleTime: 10000,
  });
};

/**
 * Hook para cargar enrollments rechazados
 */
export const useRejectedEnrollments = () => {
  const { setRejected } = useEnrollmentsStore();

  return useQuery({
    queryKey: QUERY_KEYS.rejected,
    queryFn: async () => {
      const rejected = await enrollmentsApi.getRejectedEnrollments();
      setRejected(rejected);
      return rejected;
    },
    refetchInterval: 60000, // Refetch cada 60s como fallback (el WS empuja en vivo)
    staleTime: 30000,
  });
};

/**
 * Mutation para aprobar enrollment
 */
export const useApproveEnrollment = () => {
  const queryClient = useQueryClient();
  const { removeEnrollment } = useEnrollmentsStore();

  return useMutation({
    mutationFn: enrollmentsApi.approveEnrollment,
    onSuccess: (updatedEnrollment, enrollmentId) => {
      // Only remove locally if backend confirmed approval.
      if (updatedEnrollment?.status === 'approved') {
        removeEnrollment(enrollmentId);
      } else {
        console.warn('[Enrollments] approve returned non-approved status', updatedEnrollment);
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.enrollments });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.rejected });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

/**
 * Mutation para eliminar enrollment
 */
export const useDeleteEnrollment = () => {
  const queryClient = useQueryClient();
  const { removeEnrollment } = useEnrollmentsStore();

  return useMutation({
    mutationFn: enrollmentsApi.deleteEnrollment,
    onSuccess: (_, enrollmentId) => {
      removeEnrollment(enrollmentId);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.enrollments });
    },
  });
};
